import { usePassportProvider } from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { useQuest } from "@/context/QuestProvider";
import { updateQuestWithAutoHide } from "@/context/QuestProvider/questActionHandlers";
import { useAccessToken } from "@/hooks";
import { notifyError } from "@/utils/monitoring";
import { authorizedPost } from "@/utils/request";
import { delay } from "@/utils/util";
import { trackLegacyEvent } from "@analytics";
import { useGetWalletLatestEarnings } from "@hooks/api";
import type { QuestState } from "@ui-kit/QuestList";
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
} from "react";

const API_PATH = "/v1/rewards/claim";

export const submitQuestCompletion = async (
  eventId: string,
  getAccessToken: () => Promise<string | undefined>,
): Promise<Error | undefined> => {
  try {
    // Required to ensure that the access token contains the zk address
    const accessToken = await getAccessToken();
    if (accessToken === undefined) {
      throw new Error("Unauthorized");
    }

    await authorizedPost(API_PATH, { event: eventId }, accessToken);
    return undefined;
  } catch (e) {
    notifyError(e, "questCompletionError", { event: eventId });
    return e as Error;
  }
};

const trackQuestClick = (ruleId: string, extras?: Record<string, unknown>) => {
  trackLegacyEvent({
    screen: "Details",
    userJourney: "Onboarding",
    action: "Pressed",
    control: ruleId,
    controlType: "Button",
    extras: {
      ...extras,
      userJourney: "Games",
      ruleId,
    },
  });
};

export const useSubmitQuest = (
  setSelectedQuest: Dispatch<SetStateAction<QuestState | undefined>>,
  onWishlistClick: (identifier: string) => Promise<void>,
  asyncCompleteHandler: MutableRefObject<(() => Promise<unknown>) | undefined>,
) => {
  const { dispatchAction } = useQuest((store) => store);
  const { passportState } = usePassportProvider();
  const getAccessToken = useAccessToken();
  const { walletAddress } = usePassportProvider();
  const { mutate: mutateGetEarnings } =
    useGetWalletLatestEarnings(walletAddress);

  const getCompleteHandler = useCallback(
    (
      quest: QuestState,
      onWishlistClick: (identifier: string) => Promise<void>,
    ) => {
      switch (quest.key) {
        case "follow":
          return async () => {
            await onWishlistClick(quest.key);
          };
        case "watch":
        case "quiz":
        case "bonus":
        case "bonus-variant-2":
        case "ownership":
          return async () => {
            asyncCompleteHandler.current = () =>
              submitQuestCompletion(quest.eventId, getAccessToken);
          };
        case "website":
        case "telegram":
        case "telegram-bot-connect":
          return async () => {
            const error = await submitQuestCompletion(
              quest.eventId,
              getAccessToken,
            );
            await delay(10000);
            return error;
          };
        case "x-v2":
        case "discord-v2":
        case "x-share-validation":
        case "x-post-tweet-validation":
        case "x-like-and-retweet-validation":
        case "telegram-v2":
        case "social-engagement":
          return async () => {
            asyncCompleteHandler.current = async () => {
              await updateQuestWithAutoHide(dispatchAction, {
                ...quest,
                status: "in-progress",
              });
              await delay(1000);
              return undefined;
            };
          };
        default:
          return () => Promise.reject(new Error("Unrecognised quest type"));
      }
    },
    [dispatchAction, getAccessToken, asyncCompleteHandler],
  );

  const { openLoginModal } = useLoginModal();

  const submitQuest = useCallback(
    async (quest: QuestState) => {
      if (!passportState.authenticated) {
        openLoginModal();
        return;
      }

      setSelectedQuest(quest);

      // if quest is dark machine special tweet link out quest, we don't update with loading state
      // as this quest cannot be completed
      if (quest.key === "bonus-variant-2") {
        return;
      }

      if (quest.key === "daily-game-play") {
        return;
      }

      if (quest.key === "website" && quest.icon === "Twitch") {
        return;
      }

      await updateQuestWithAutoHide(dispatchAction, {
        ...quest,
        status: "in-progress",
      });

      try {
        trackQuestClick(quest.ruleId, {
          completedAt: quest.completedAt,
          lockedUntil: quest.lockedUntil,
          waitTimeInHrs: quest.waitTimeInHrs,
          controlType: "Button",
          controlLabel: quest.label,
          gemsAmount: quest.increase,
          onboardingType: quest.type,
          eventId: quest.eventId,
          link: quest.linkUrl,
        });

        const completeHandler = getCompleteHandler(quest, onWishlistClick);
        const error = await completeHandler();
        if (error) {
          throw error;
        }

        if (!asyncCompleteHandler.current) {
          await updateQuestWithAutoHide(dispatchAction, {
            ...quest,
            locked: false,
            completed: true,
            status: "completed",
          });
          asyncCompleteHandler.current = undefined;
        }
      } catch (err) {
        await updateQuestWithAutoHide(dispatchAction, {
          ...quest,
          completed: false,
          status: "pending",
          // Preserve the original locked state from the quest
          locked: quest.locked,
        });
        notifyError(
          `Failed to complete onboarding quest ${quest.ruleId}`,
          "quests",
        );
      } finally {
        mutateGetEarnings();
      }
    },
    [
      dispatchAction,
      getCompleteHandler,
      mutateGetEarnings,
      onWishlistClick,
      openLoginModal,
      setSelectedQuest,
      passportState.authenticated,
      asyncCompleteHandler,
    ],
  );

  return { submitQuest };
};
